import type { UserRole, PermissionAction, RolePreset, RolesUser } from '@shared/types';

export type { PermissionAction, RolePreset, RolesUser };

export interface AuthState {
  user: import('@shared/types').User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export interface LoginResult {
  success: boolean;
  error?: string;
  mfaError?: import('firebase/auth').MultiFactorError;
}

type RbacAction = 'create' | 'read' | 'update' | 'delete';

export interface RBACPermission {
  resource: string;
  actions: RbacAction[];
}

// プリセット権限（後方互換用 — 新規コードは RolePreset を使うこと）
export interface PresetPermission {
  resource: string;
  actions: PermissionAction[];
}

function perms(resources: readonly string[], actions: readonly RbacAction[]): RBACPermission[] {
  return resources.map((resource) => ({ resource, actions: [...actions] }));
}

// 一般ロールが作成・更新できる通常業務リソース。
const BUSINESS_WRITE: readonly string[] = [
  'customers',
  'quotes',
  'orders',
  'purchase_orders',
  'customer_pricing',
  'invoices',
  'subcontractors',
  'subcontract_orders',
  'suppliers',
  'procurement_orders',
  'deliveries',
  'combined_invoices',
];

// 管理者のみ編集可、全ロール閲覧可のマスタ群。
const MASTERS: readonly string[] = [
  'products',
  'limitTypes',
  'paymentDates',
  'units',
  'banks',
  'businessTypes',
  'tradeTypes',
  'employeeCategories',
  'employees',
  'usergroups',
  'companyProfile',
];

// 全ロールが閲覧できるリソース（閲覧専用ロールは廃止のため最下位の一般も全閲覧可）。
const READ_ALL: readonly string[] = [
  ...BUSINESS_WRITE,
  ...MASTERS,
  'documents',
  'payments',
  'monthly_closings',
  'departments',
  'organizations',
  'presets',
];

// 一般: 通常業務の作成・更新＋全閲覧。請求書は下書き作成まで（発行遷移は Firestore ルールで制御）。
const generalPolicies: RBACPermission[] = [
  ...perms(READ_ALL, ['read']),
  ...perms(BUSINESS_WRITE, ['create', 'update']),
  { resource: 'documents', actions: ['create', 'read'] },
  { resource: 'counters', actions: ['create', 'read', 'update'] },
];

// 経理: 一般＋入金登録＋月次締め＋請求書発行。
const accountingPolicies: RBACPermission[] = [
  ...generalPolicies,
  { resource: 'payments', actions: ['create', 'read'] },
  { resource: 'monthly_closings', actions: ['create', 'read'] },
];

// 管理者: 経理＋マスタ/ユーザー/部署の管理＋業務データ削除。テナント・権限テンプレは閲覧のみ。
const adminPolicies: RBACPermission[] = [
  ...accountingPolicies,
  ...perms([...BUSINESS_WRITE, 'documents', 'payments'], ['delete']),
  ...perms(MASTERS, ['create', 'read', 'update', 'delete']),
  ...perms(['users', 'departments'], ['create', 'read', 'update', 'delete']),
  ...perms(['organizations', 'presets', 'user_roles'], ['read']),
];

// システム管理者: すべての操作が可能。
const systemAdminPolicies: RBACPermission[] = [
  { resource: '*', actions: ['create', 'read', 'update', 'delete'] },
];

export const RBAC_POLICIES: Record<UserRole, RBACPermission[]> = {
  system_admin: systemAdminPolicies,
  admin: adminPolicies,
  accounting: accountingPolicies,
  general: generalPolicies,
};
